// Billing Settlement panel (Office Admin / Owner view; restricted roles get a status-only version).
// Every figure is derived from real data: the billable amount is the estimate's contract value
// (job card) and the rest is computed from the engagement's invoices. Nothing is hardcoded — when
// there is no accepted value yet the cell shows "—" rather than a made-up number.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { InvoiceLifecycleList } from '@/components/Workflow/InvoiceLifecycleList';
import { isActiveInvoice } from '@/components/Workflow/invoiceStatus';
import type { Invoice } from '@/redux/internal-services/jobsApi';
import { useSamSectionContext } from '@/hooks';

interface Props {
  engagementId: string;
  invoices: Invoice[];
  /** Estimate contract value; null/undefined until an accepted estimate exists. */
  billableAmount: number | null | undefined;
  isSettled: boolean;
  closedEarly?: boolean;
  /** Viewer's role may not see money (workflow-definition `invoice_view_amounts`). */
  amountsRestricted: boolean;
  canPrepare: boolean;
  permsLoaded: boolean;
  onCreateInvoice: () => void;
}

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Figure: React.FC<{ label: string; value: string; tone?: 'ok' | 'warn' }> = ({ label, value, tone }) => (
  <div className="min-w-0 border-r border-ui-border px-3 py-3 last:border-r-0">
    <p className="eyebrow">{label}</p>
    <p className={`mt-1 text-body font-semibold ${tone === 'ok' ? 'text-success-text' : tone === 'warn' ? 'text-warning-text' : 'text-ui-text'}`}>
      {value}
    </p>
  </div>
);

export const BillingSettlementPanel: React.FC<Props> = ({
  engagementId, invoices, billableAmount, isSettled, closedEarly, amountsRestricted, canPrepare, permsLoaded, onCreateInvoice,
}) => {
  const { t } = useTranslation();
  useSamSectionContext('billing', { engagement_id: engagementId });
  const active = invoices.filter(isActiveInvoice);
  const cancelled = invoices.filter((i) => i.status === 'Cancelled').length;
  const invoiced = active.reduce((s, i) => s + (i.amount ?? 0), 0);
  const paid = active.reduce((s, i) => s + (i.paid ?? 0), 0);
  const outstanding = invoiced - paid;
  const remaining = billableAmount != null ? billableAmount - invoiced : null;

  let statusLine: string;
  if (isSettled) statusLine = t('billing.panel.settled', 'Billing settled — every billable amount is invoiced and paid.');
  else if (invoices.length === 0) statusLine = t('billing.panel.none', 'No invoicing yet — an authorized user may raise an invoice whenever it is required.');
  else if (remaining != null && remaining > 0.005) statusLine = t('billing.panel.toInvoice', 'Not settled — {{amount}} still to invoice.', { amount: money(remaining) });
  else if (outstanding > 0.005) statusLine = t('billing.panel.outstanding', 'Not settled — {{amount}} still outstanding on active invoices.', { amount: money(outstanding) });
  else statusLine = t('billing.panel.ready', 'Ready to settle — nothing left to invoice, no balance.');

  const countLabel = invoices.length === 0
    ? t('billing.panel.noneRaised', 'No invoices raised yet')
    : [
      t('billing.panel.raised', '{{count}} raised', { count: invoices.length }),
      t('billing.panel.active', '{{count}} active', { count: active.length }),
      ...(cancelled ? [t('billing.panel.cancelled', '{{count}} cancelled', { count: cancelled })] : []),
    ].join(' · ');

  const canCreate = !isSettled && !closedEarly && canPrepare;

  return (
    <div className="border-b border-ui-border px-[18px] py-4">
      <div className="overflow-hidden rounded-card border border-ui-border bg-ui-card">
        <div className="flex items-start gap-3 px-3.5 py-3">
          <div className="min-w-0 flex-1">
            <p className="eyebrow">{t('billing.panel.title', 'Billing settlement')}</p>
            <p className="mt-0.5 text-caption text-ui-text3">{t('billing.panel.excluded', 'Cancelled and void invoices excluded from totals')}</p>
          </div>
          <Badge variant={isSettled ? 'success' : 'warning'}>{isSettled ? t('billing.panel.badgeSettled', 'BILLING SETTLED') : t('billing.panel.badgeNotSettled', 'NOT SETTLED')}</Badge>
        </div>

        {amountsRestricted ? (
          <p className="border-t border-ui-border px-3.5 py-3 text-caption text-ui-text2">
            {t('billing.panel.restricted', "Financial totals are restricted for your role. Billing amounts are visible to Owner and Office Admin; you can still follow each invoice's status below.")}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto border-t border-ui-border">
            <div className="grid min-w-[480px] grid-cols-5">
              <Figure label={t('billing.panel.billable', 'Billable amount')} value={billableAmount != null ? money(billableAmount) : '—'} />
              <Figure label={t('billing.panel.invoiced', 'Invoiced (active)')} value={money(invoiced)} />
              <Figure label={t('billing.panel.received', 'Payments received')} value={money(paid)} tone={paid > 0 ? 'ok' : undefined} />
              <Figure label={t('billing.panel.remaining', 'Remaining uninvoiced')} value={remaining != null ? money(Math.max(remaining, 0)) : '—'} />
              <Figure label={t('billing.panel.outstandingBalance', 'Outstanding balance')} value={money(outstanding)} tone={outstanding > 0.005 ? 'warn' : undefined} />
            </div>
            </div>
            <p className="border-t border-ui-border px-3.5 py-2.5 text-caption text-ui-text2">{statusLine}</p>
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-ui-border px-3.5 py-2.5">
          <p className="eyebrow flex-1">{t('billing.panel.listTitle', 'Invoices on this engagement')}</p>
          <p className="text-caption text-ui-text3">{countLabel}</p>
          {canCreate && (
            <Button type="button" size="xs" variant="outline" onClick={onCreateInvoice}>{t('billing.panel.create', '+ Create invoice')}</Button>
          )}
        </div>

        <InvoiceLifecycleList engagementId={engagementId} invoices={invoices} />

        {!isSettled && !closedEarly && permsLoaded && !canPrepare && (
          <p className="border-t border-dashed border-ui-border bg-ui-subtle px-3.5 py-3 text-caption text-ui-text3">
            {t('billing.panel.noPrepare', 'Invoice preparation is not available to your role — it requires the Invoice Preparation privilege held by Owner and Office Admin.')}
          </p>
        )}
      </div>
    </div>
  );
};
